import React, { useState } from 'react';
import { X, Trash2, Save, Mail, Store, User, Users, AlertTriangle, Check } from 'lucide-react';
import {
  Order,
  OrderCustomerData,
  OrderItem,
  orderReference,
  updateOrder,
  deleteOrder,
} from '../../services/orders';
import { sendConfirmationEmail, EmailRecipient } from '../../services/orderEmail';

interface Props {
  order: Order;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}

const STATUS_OPTIONS = ['pending', 'paid', 'processing', 'shipped', 'completed', 'cancelled', 'refunded'];

export default function OrderDetailDrawer({ order, onClose, onSaved, onDeleted }: Props) {
  const cd = order.customer_data || {};
  const [status, setStatus] = useState(order.status || 'pending');
  const [amount, setAmount] = useState(String(order.amount ?? ''));
  const [form, setForm] = useState<OrderCustomerData>({
    email: cd.email || '',
    firstName: cd.firstName || '',
    lastName: cd.lastName || '',
    phone: cd.phone || '',
    address: cd.address || '',
    company: cd.company || '',
    order_comment: cd.order_comment || '',
    payment_method: cd.payment_method || '',
    shipping_method: cd.shipping_method || '',
  });
  const [items, setItems] = useState<OrderItem[]>(cd.items || []);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');
  const [emailStatus, setEmailStatus] = useState<{ type: 'ok' | 'err'; msg: string } | null>(null);
  const [sendingMode, setSendingMode] = useState<EmailRecipient | null>(null);

  const set = (field: keyof OrderCustomerData, value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const updateItem = (idx: number, field: keyof OrderItem, value: string) => {
    setItems((prev) =>
      prev.map((it, i) =>
        i === idx
          ? { ...it, [field]: field === 'price' || field === 'quantity' ? Number(value) || 0 : value }
          : it
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await updateOrder(order.id, {
        status,
        amount: parseFloat(amount) || 0,
        marketing_consent: order.marketing_consent,
        customer_data: {
          ...cd,
          ...form,
          items,
          total: parseFloat(amount) || cd.total || 0,
        },
      });
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Salvestamine ebaõnnestus');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      await deleteOrder(order.id);
      onDeleted();
    } catch (err: any) {
      setError(err.message || 'Kustutamine ebaõnnestus');
      setDeleting(false);
    }
  };

  const handleResend = async (recipient: EmailRecipient) => {
    setSendingMode(recipient);
    setEmailStatus(null);
    try {
      const sentTo = await sendConfirmationEmail(
        {
          orderId: orderReference(order),
          email: form.email || '',
          customerName: `${form.firstName || ''} ${form.lastName || ''}`.trim(),
          customerPhone: form.phone,
          shippingMethod: form.shipping_method,
          shippingAddress: form.address,
          paymentMethod: form.payment_method,
          orderTotal: parseFloat(amount) || 0,
          items,
        },
        recipient
      );
      setEmailStatus({ type: 'ok', msg: `Saadetud: ${sentTo.join(', ')}` });
    } catch (err: any) {
      setEmailStatus({ type: 'err', msg: err?.text || err?.message || 'E-kirja saatmine ebaõnnestus' });
    } finally {
      setSendingMode(null);
    }
  };

  const createdAt = new Date(order.created_at).toLocaleString('et-EE');

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-2xl bg-gray-50 h-full overflow-y-auto shadow-2xl flex flex-col">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Tellimus #{orderReference(order)}</h2>
            <p className="text-xs text-gray-500">{createdAt}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-900 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 flex-1">
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Resend confirmation email */}
          <section className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-4 h-4 text-[#4FC1E9]" />
              <h3 className="font-semibold text-gray-900 text-sm">Saada kinnituskiri uuesti</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                onClick={() => handleResend('shop')}
                disabled={sendingMode !== null}
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:border-[#4FC1E9] hover:text-[#4FC1E9] transition disabled:opacity-50"
              >
                <Store className="w-4 h-4" />
                {sendingMode === 'shop' ? 'Saadan...' : 'Ainult poele'}
              </button>
              <button
                onClick={() => handleResend('customer')}
                disabled={sendingMode !== null}
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:border-[#4FC1E9] hover:text-[#4FC1E9] transition disabled:opacity-50"
              >
                <User className="w-4 h-4" />
                {sendingMode === 'customer' ? 'Saadan...' : 'Ainult kliendile'}
              </button>
              <button
                onClick={() => handleResend('both')}
                disabled={sendingMode !== null}
                className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-[#4FC1E9] text-white text-sm hover:bg-[#3ba8d0] transition disabled:opacity-50"
              >
                <Users className="w-4 h-4" />
                {sendingMode === 'both' ? 'Saadan...' : 'Mõlemale'}
              </button>
            </div>
            {emailStatus && (
              <div
                className={`mt-3 flex items-center gap-2 text-sm ${
                  emailStatus.type === 'ok' ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {emailStatus.type === 'ok' ? (
                  <Check className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                )}
                <span>{emailStatus.msg}</span>
              </div>
            )}
            <p className="text-xs text-gray-400 mt-2">Poe aadress: ametliktouks@gmail.com</p>
          </section>

          {/* Order meta */}
          <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 text-sm">Tellimuse info</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Staatus">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="admin-input"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Summa (€)">
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="admin-input"
                />
              </Field>
            </div>
            <Field label="ESTO / makse viide">
              <input value={order.esto_reference || '—'} disabled className="admin-input opacity-60" />
            </Field>
          </section>

          {/* Customer */}
          <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 text-sm">Kliendi andmed</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Eesnimi">
                <input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} className="admin-input" />
              </Field>
              <Field label="Perenimi">
                <input value={form.lastName} onChange={(e) => set('lastName', e.target.value)} className="admin-input" />
              </Field>
            </div>
            <Field label="E-post">
              <input value={form.email} onChange={(e) => set('email', e.target.value)} className="admin-input" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Telefon">
                <input value={form.phone} onChange={(e) => set('phone', e.target.value)} className="admin-input" />
              </Field>
              <Field label="Ettevõte">
                <input value={form.company} onChange={(e) => set('company', e.target.value)} className="admin-input" />
              </Field>
            </div>
            <Field label="Transport">
              <input value={form.shipping_method} onChange={(e) => set('shipping_method', e.target.value)} className="admin-input" />
            </Field>
            <Field label="Aadress / pakiautomaat">
              <input value={form.address} onChange={(e) => set('address', e.target.value)} className="admin-input" />
            </Field>
            <Field label="Makseviis">
              <input value={form.payment_method} onChange={(e) => set('payment_method', e.target.value)} className="admin-input" />
            </Field>
            <Field label="Kommentaar">
              <textarea
                value={form.order_comment}
                onChange={(e) => set('order_comment', e.target.value)}
                rows={2}
                className="admin-input resize-none"
              />
            </Field>
          </section>

          {/* Items */}
          <section className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="font-semibold text-gray-900 text-sm">Tooted ({items.length})</h3>
            {items.length === 0 && <p className="text-sm text-gray-400">Tooteid pole.</p>}
            {items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                <input
                  value={it.name}
                  onChange={(e) => updateItem(idx, 'name', e.target.value)}
                  className="admin-input col-span-7"
                  placeholder="Toode"
                />
                <input
                  type="number"
                  value={it.quantity}
                  onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                  className="admin-input col-span-2 text-center"
                  placeholder="Kogus"
                />
                <input
                  type="number"
                  step="0.01"
                  value={it.price}
                  onChange={(e) => updateItem(idx, 'price', e.target.value)}
                  className="admin-input col-span-3 text-right"
                  placeholder="Hind"
                />
              </div>
            ))}
          </section>
        </div>

        {/* Footer actions */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between gap-3">
          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              className="flex items-center gap-2 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition"
            >
              <Trash2 className="w-4 h-4" />
              Kustuta
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Kindel?</span>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Kustutan...' : 'Jah, kustuta'}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-3 py-2 text-gray-600 rounded-lg text-sm hover:bg-gray-100"
              >
                Tühista
              </button>
            </div>
          )}

          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-[#4FC1E9] text-white rounded-lg text-sm font-medium hover:bg-[#3ba8d0] transition disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Salvestan...' : 'Salvesta'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
